import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  browserLocalPersistence,
  browserSessionPersistence,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../../firebase";
import {
  getSafeAccessError,
  platformAccessApi,
} from "../api/platformAccessApi";
import { APPLICANT_WORKSPACE_ROUTE } from "../auth/accessRouting";
import { routeAfterCentralLogin, useAccess } from "../auth/AccessProvider";
import AccessAlert from "../components/AccessAlert";
import AccessShell from "../components/AccessShell";
import { sendBrandedVerificationEmail } from "../utils/sendVerificationEmail";

function isEmailVerificationRequired(error) {
  return (
    error?.code === "functions/failed-precondition" &&
    error?.details?.reason === "email-verification-required"
  );
}

function isLegacyMigrationInvalidCredential(error) {
  return (
    error?.code === "functions/unauthenticated" &&
    error?.details?.reason === "legacy-migration-invalid-credential"
  );
}

export default function CentralLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    accessContext,
    loading: contextLoading,
    refreshAccessContext,
  } = useAccess();
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");

  const signInWithLegacyOpportunityFallback = async (
    submittedEmail,
    password,
  ) => {
    try {
      return await signInWithEmailAndPassword(auth, submittedEmail, password);
    } catch (signInError) {
      const migratableCodes = new Set([
        "auth/invalid-credential",
        "auth/invalid-login-credentials",
        "auth/user-not-found",
        "auth/wrong-password",
      ]);
      if (!migratableCodes.has(signInError?.code)) throw signInError;
      const migration = await httpsCallable(
        functions,
        "talentLenzOpportunityInvitation",
      )({
        action: "migrateLegacyAccount",
        email: submittedEmail,
        password,
      });
      if (migration?.data?.passwordResetRequired) {
        await sendPasswordResetEmail(auth, submittedEmail);
        const resetRequiredError = new Error("Password reset required.");
        resetRequiredError.code = "opportunity/password-reset-required";
        throw resetRequiredError;
      }
      return signInWithEmailAndPassword(auth, submittedEmail, password);
    }
  };

  useEffect(() => {
    if (!submitting && user && accessContext && !contextLoading) {
      navigate(routeAfterCentralLogin(accessContext), { replace: true });
    }
  }, [user, accessContext, contextLoading, navigate, submitting]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const submittedEmail = String(data.get("email") || "").trim();
    const password = String(data.get("password") || "");
    const remember = data.get("remember") === "on";
    setEmail(submittedEmail);
    setError("");
    setMessage("");
    setSubmitting(true);
    try {
      await setPersistence(
        auth,
        remember ? browserLocalPersistence : browserSessionPersistence,
      );
      const credential = await signInWithLegacyOpportunityFallback(
        submittedEmail,
        password,
      );
      const tokenResult = await credential.user.getIdTokenResult(true);
      const isPlatformAdmin = tokenResult.claims?.platformAdmin === true;
      if (!tokenResult.claims?.tenantRole && !isPlatformAdmin) {
        // A just-established Firebase session can briefly be unavailable to
        // the direct access-context read. Treat only that transient condition
        // as an unknown context and continue through the central-access
        // recovery path below.
        let existingAccess = null;
        if (credential.user.emailVerified) {
          try {
            existingAccess = await platformAccessApi.getAccessContext();
          } catch (accessError) {
            if (accessError?.code !== "functions/unauthenticated") {
              throw accessError;
            }
          }
        }
        if (existingAccess?.activeTenant) {
          await platformAccessApi.setActiveTenant(
            existingAccess.activeTenant.tenantId,
          );
        } else if (!existingAccess) {
          // Only a missing access read should try to link an old opportunity
          // profile. An applicant who already resolved with no company does
          // not need that repair, and a rejected repair must not block sign-in.
          const ensureCentralAccess = httpsCallable(
            functions,
            "talentLenzOpportunityInvitation",
          );
          try {
            await ensureCentralAccess({ action: "ensureCentralAccess" });
          } catch (ensureError) {
            if (ensureError?.code !== "functions/unauthenticated") {
              throw ensureError;
            }
            try {
              await credential.user.getIdToken(true);
              await ensureCentralAccess({ action: "ensureCentralAccess" });
            } catch (retryError) {
              if (retryError?.code !== "functions/unauthenticated")
                throw retryError;
            }
          }
        }
      }
      let context;
      try {
        context = await refreshAccessContext({
          forceTokenRefresh: true,
          authUser: credential.user,
        });
      } catch (contextError) {
        // Same just-established-session race as the retries above: the
        // claims read can briefly fail right after sign-in. Retry once with
        // a forced fresh token instead of failing the sign-in outright.
        if (contextError?.code !== "functions/unauthenticated")
          throw contextError;
        await credential.user.getIdToken(true);
        try {
          context = await refreshAccessContext({
            forceTokenRefresh: true,
            authUser: credential.user,
          });
        } catch (retryError) {
          if (retryError?.code !== "functions/unauthenticated")
            throw retryError;
          if (tokenResult.claims?.tenantRole || isPlatformAdmin)
            throw retryError;
          context = null;
        }
      }
      // Unverified accounts are admitted only by the scoped candidate policy.
      // Do not replay an unrelated protected route that would send that candidate
      // into an administrator/member verification gate.
      const requestedRoute = credential.user.emailVerified
        ? location.state?.from
        : null;
      const defaultRoute =
        routeAfterCentralLogin(context) || APPLICANT_WORKSPACE_ROUTE;
      const requestedPath =
        typeof requestedRoute === "string"
          ? requestedRoute
          : requestedRoute?.pathname;
      const returningToOpportunity =
        typeof requestedPath === "string" &&
        /^\/opportunity-page\/?(?:[?#]|$)/.test(requestedPath);
      const returningToTenantWorkspace =
        typeof requestedPath === "string" &&
        /^\/(?:tenant-admin|systems|dashboard)(?:\/|[?#]|$)/.test(
          requestedPath,
        );
      const useDefaultRoute =
        defaultRoute === APPLICANT_WORKSPACE_ROUTE ||
        defaultRoute === "/unity/downloads" ||
        (defaultRoute === "/platform-admin/dashboard" &&
          returningToTenantWorkspace) ||
        (defaultRoute === "/unity/learner" && returningToOpportunity);
      navigate(
        useDefaultRoute
          ? defaultRoute
          : requestedRoute && defaultRoute !== "/access-pending"
            ? requestedRoute
            : defaultRoute,
        { replace: true },
      );
    } catch (nextError) {
      if (
        !auth.currentUser?.emailVerified &&
        isEmailVerificationRequired(nextError)
      ) {
        await sendBrandedVerificationEmail(auth.currentUser).catch(() => {});
        setError(
          "Verify your email before signing in. You can resend the verification message below.",
        );
        setMessage("We sent a verification link to your email address.");
      } else if (nextError?.code === "opportunity/password-reset-required") {
        setError(
          "Your previous Opportunity Portal password is too short for the new sign-in system.",
        );
        setMessage(
          "We sent a password reset link to your email. Set a new password, then sign in here.",
        );
      } else if (isLegacyMigrationInvalidCredential(nextError)) {
        setError("The email or password is incorrect.");
      } else {
        setError(
          getSafeAccessError(
            nextError,
            "We could not sign you in. Check your details and try again.",
          ),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    const resetEmail = String(email || "")
      .trim()
      .toLowerCase();
    setMessage("");
    if (!resetEmail) {
      setError("Enter your work email first, then choose Forgot password.");
      return;
    }
    setError("");
    navigate(`/reset-password?email=${encodeURIComponent(resetEmail)}`);
  };

  const handleResendVerification = async () => {
    if (!auth.currentUser) {
      setError(
        "Sign in with your credentials first so we can resend verification.",
      );
      return;
    }
    try {
      await sendBrandedVerificationEmail(auth.currentUser);
      setMessage("A new verification email has been sent.");
    } catch (nextError) {
      setError(
        getSafeAccessError(
          nextError,
          "We could not resend verification. Please wait and try again.",
        ),
      );
    }
  };

  return (
    <AccessShell
      eyebrow="Sign in"
      title="Welcome back."
      description="Access your AndUs workspace."
      footer={
        <p>
          Platform administrator?{" "}
          <Link to="/platform-admin/register">Create account</Link>
        </p>
      }
    >
      <AccessAlert tone="error">{error}</AccessAlert>
      <AccessAlert tone="success">{message}</AccessAlert>
      <form className="pa-form pa-login-form" onSubmit={handleSubmit}>
        <div className="pa-field">
          <span className="pa-field__label-row">
            <label htmlFor="central-login-email">Work email</label>
            <button type="button" onClick={handleForgotPassword}>
              Forgot password?
            </button>
          </span>
          <div className="pa-input-control">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 6.75h16v10.5H4zM4.5 7.5 12 13l7.5-5.5" />
            </svg>
            <input
              id="central-login-email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="name@company.co.za"
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </div>
        </div>
        <label className="pa-field">
          <span>Password</span>
          <div className="pa-input-control">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M7.5 10V7.5a4.5 4.5 0 0 1 9 0V10M5.5 10h13v10h-13z" />
            </svg>
            <input
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              placeholder="Enter your password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((visible) => !visible)}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </label>
        <label className="pa-check">
          <input name="remember" type="checkbox" />
          <span aria-hidden="true" />
          <span>Keep me signed in on this trusted device</span>
        </label>
        <button
          className="pa-primary-button"
          type="submit"
          disabled={submitting}
        >
          <span>{submitting ? "Signing in…" : "Sign in"}</span>
          <span aria-hidden="true">›</span>
        </button>
      </form>
      {error.includes("Verify your email") ? (
        <button
          className="pa-text-button"
          type="button"
          onClick={handleResendVerification}
        >
          Resend verification email
        </button>
      ) : null}
      <p className="pa-invite-note">
        Added to an opportunity for the first time? Activate your account using
        the registration link sent by your administrator.
      </p>
      <p className="pa-invite-note">
        Tenant administrators register through the secure invitation link sent
        by their Platform Administrator.
      </p>
    </AccessShell>
  );
}
